import { ChevronDown } from "lucide-react";
import React from "react";
import { Updater } from "use-immer";

import { IgBadge, IgCheckbox, IgTypography } from "@intelligems/ig-theme";
import {
  StandardEventAllowedKeyMetrics,
  StandardEventDto,
} from "@intelligems/ig-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { sortStandardEvents } from "@src/features/experienceMetrics/helpers";

interface GenericAccordionSelectSectionsProps {
  selectedStandardMetrics: StandardEventAllowedKeyMetrics[];
  setSelectedStandardMetrics: Updater<StandardEventAllowedKeyMetrics[]>;
  filteredStandardEvents: Record<
    Partial<StandardEventAllowedKeyMetrics>,
    StandardEventDto
  >;
}

export const GenericAccordionSelectSections = ({
  selectedStandardMetrics,
  setSelectedStandardMetrics,
  filteredStandardEvents,
}: GenericAccordionSelectSectionsProps) => {
  const theme = useTheme();
  return (
    <Stack mt={2} direction={"column"} spacing={2}>
      {Object.entries(sortStandardEvents(filteredStandardEvents)).map(
        ([group, metrics]) => (
          <Grid key={group} sx={{ padding: "0px 40px" }}>
            <Accordion
              key={group}
              elevation={0}
              disableGutters={true}
              defaultExpanded={true}
            >
              <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                <Grid
                  container
                  direction={"row"}
                  columnSpacing={4}
                  alignItems={"center"}
                >
                  <Grid sx={{ paddingLeft: "0px !important" }}>
                    <IgTypography
                      sx={{ textTransform: "uppercase" }}
                      fontWeight={600}
                    >
                      {group} METRICS
                    </IgTypography>
                  </Grid>
                  <Grid mt={-1}>
                    <IgBadge
                      badgeContent={
                        metrics.filter((metric) =>
                          selectedStandardMetrics.includes(
                            metric.identifier as StandardEventAllowedKeyMetrics
                          )
                        ).length
                      }
                      color={"info"}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingLeft: "0px" }}>
                <Stack direction={"column"} spacing={2}>
                  {metrics.map((metric) => (
                    <Grid
                      container
                      direction={"row"}
                      columnSpacing={2}
                      key={metric.name}
                      wrap={"nowrap"}
                      alignItems={"flex-start"}
                    >
                      <Grid>
                        <IgCheckbox
                          onChange={(event) => {
                            setSelectedStandardMetrics((draft) => {
                              const metricId =
                                metric.identifier as StandardEventAllowedKeyMetrics;
                              if (event.target.checked) {
                                draft.push(metricId);
                              } else {
                                draft = draft.filter(
                                  // eslint-disable-next-line max-nested-callbacks
                                  (selectedMetric) =>
                                    selectedMetric !== metricId
                                );
                              }
                              return draft;
                            });
                          }}
                          checked={selectedStandardMetrics.includes(
                            metric.identifier as StandardEventAllowedKeyMetrics
                          )}
                        />
                      </Grid>
                      <Grid>
                        <Stack direction={"column"} sx={{ maxWidth: "490px" }}>
                          <IgTypography sx={{ fontWeight: 600 }}>
                            {metric.name}
                          </IgTypography>

                          <IgTypography
                            variant={"body2"}
                            color={theme.palette.text.secondary}
                          >
                            {metric.description}
                          </IgTypography>
                        </Stack>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )
      )}
    </Stack>
  );
};
